import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import GuidedTimeSlots from './guidedTimeSlots';
import { getSlotReservationStats } from '../../service';
import { toast } from 'react-toastify';

const ExperienceTab = forwardRef((props, ref) => {
  // State to track active tab for Parcours accompagné
  const [activeParcoursTab, setActiveParcoursTab] = useState('16.10');
  // State to store API response data
  const [introData, setIntroData] = useState({});
  const [guidedData, setGuidedData] = useState({});
  const [isIntroLoading, setIsIntroLoading] = useState(true);
  const [isGuidedLoading, setIsGuidedLoading] = useState(true);

  // Fetch intl reservation slots
  const fetchIntlReservationSlots = async () => {
    setIsIntroLoading(true);
    try {
      const response = await getSlotReservationStats('intro');
      console.log('Intl reservation data:', response.data);
      setIntroData(response.data);
    } catch (error) {
      console.error('Error fetching intl reservation slots:', error);
      toast.error('Error fetching intl reservation slots');
    } finally {
      setIsIntroLoading(false);
    }
  };

  // Fetch guided reservation slots
  const fetchGuidedReservationSlots = async () => {
    setIsGuidedLoading(true);
    try {
      const response = await getSlotReservationStats('guided');
      console.log('Guided reservation data:', response.data);
      setGuidedData(response.data);
    } catch (error) {
      console.error('Error fetching guided reservation slots:', error);
      toast.error('Error fetching guided reservation slots');
    } finally {
      setIsGuidedLoading(false);
    }
  };

  // Refresh all data
  const refreshData = () => {
    fetchIntlReservationSlots();
    fetchGuidedReservationSlots();
  };

  // Expose the refreshData method to parent component
  useImperativeHandle(ref, () => ({
    refreshData,
  }));

  // Fetch reservation data when component mounts
  useEffect(() => {
    refreshData();
  }, []);

  // Calculate percentage for each day and verify slot capacities
  Object.keys(introData).forEach((key) => {
    const data = introData[key];
    data.percentage = Math.round((data.total / data.capacity) * 100);
    data.isOverCapacity = data.total > data.capacity;

    // Calculate if any slots are over capacity
    data.slots.forEach((slot) => {
      slot.isOverCapacity = slot.count > slot.capacity;
    });
  });

  // Skeleton loading component for a single column of time slots
  const SkeletonTimeSlots = ({ label }) => {
    return (
      <div>
        <h3 className="text-xl font-semibold mb-4">{label}</h3>

        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex justify-end mb-2">
            <span className="font-semibold text-xs">Reservations</span>
          </div>

          {/* Skeleton slots - 8 slots to match the sample data structure */}
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              className="flex justify-between py-1 text-xs font-semibold px-2 -mx-2 rounded">
              <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-24"></div>
              <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-16"></div>
            </div>
          ))}

          <hr className="border-gray-40 my-2" />
          <div className="flex justify-between text-xs font-semibold px-2 -mx-2 rounded">
            <span>Total</span>
            <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-16"></div>
          </div>

          <hr className="border-gray-40 my-2" />
          <div className="space-y-1.5 text-xs font-semibold">
            <div className="flex justify-between">
              <span>Capacité</span>
              <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-10"></div>
            </div>
            <div className="relative h-2 w-full bg-gray-40 rounded-full">
              <div
                className="absolute top-0 left-0 h-full rounded-full bg-blue-100 animate-pulse"
                style={{ width: '50%' }}
              />
            </div>
            <div className="flex justify-between text-gray-500">
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse w-8"></div>
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse w-8"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full bg-white">
      <h2 className="text-lg font-semibold text-gray-800 mb-2">Introduction à la visite</h2>

      <div className="border border-gray-300 rounded-lg p-6">
        {isIntroLoading ? (
          <div className="grid grid-cols-2 gap-4 text-gray-50">
            <SkeletonTimeSlots label="16.10" />
            <SkeletonTimeSlots label="17.10" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-gray-50">
            {/* Ensure consistent order: Thursday (thu), Friday (fri) - removed Total (total) */}
            {['thu', 'fri'].map((key) => {
              if (!introData[key]) return null;
              const data = introData[key];
              const displayPercentage = Math.min(data.percentage, 100); // Cap at 100% for display

              return (
                <div key={key}>
                  <h3 className={`text-xl font-semibold mb-4`}>{data.label}</h3>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex justify-end mb-2">
                      <span className="font-semibold text-xs">Reservations</span>
                    </div>

                    {data.slots.map((slot, index) => {
                      return (
                        <div
                          key={`${key}-${index}`}
                          className={`flex justify-between py-1 text-xs font-semibold transition-colors duration-200 px-2 -mx-2 rounded`}>
                          <span
                            className={`${slot.isOverCapacity ? 'text-red-500' : 'text-gray-50'}`}>
                            {slot.time}
                          </span>
                          <span className={`${slot.isOverCapacity ? 'text-red-500' : ''}`}>
                            {slot.count} / {slot.capacity}
                          </span>
                        </div>
                      );
                    })}
                    <hr className="border-gray-40 my-2" />
                    <div className="flex justify-between text-xs font-semibold px-2 -mx-2 rounded">
                      <span>Total</span>
                      <span className={data.isOverCapacity ? 'text-red-500' : ''}>
                        {data.total} / {data.capacity}
                      </span>
                    </div>
                    <hr className="border-gray-40 my-2" />
                    <div className="space-y-1.5 text-xs font-semibold">
                      <div className="flex justify-between">
                        <span>Capacité</span>
                        <span className={data.isOverCapacity ? 'text-red-500' : ''}>
                          {data.percentage}%
                        </span>
                      </div>
                      <div className="relative h-2 w-full bg-gray-40 rounded-full">
                        <div
                          className={`absolute top-0 left-0 h-full rounded-full ${
                            data.isOverCapacity ? 'bg-red-500' : 'bg-blue-10'
                          }`}
                          style={{ width: `${displayPercentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>{data.total}</span>
                        <span>{data.capacity}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Second section - Parcours accompagné */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Parcours accompagné</h2>

        <div className="mb-4">
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
                activeParcoursTab === '16.10'
                  ? 'bg-blue-50 text-blue-10 border-blue-10'
                  : 'bg-white text-gray-50 hover:bg-gray-100 border-gray-300'
              }`}
              onClick={() => setActiveParcoursTab('16.10')}>
              16.10
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-r-lg border ${
                activeParcoursTab === '17.10'
                  ? 'bg-blue-50 text-blue-10 border-blue-10'
                  : 'bg-white text-gray-50 hover:bg-gray-100 border-gray-300'
              }`}
              onClick={() => setActiveParcoursTab('17.10')}>
              17.10
            </button>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded-lg p-6">
          {activeParcoursTab === '16.10' && (
            <GuidedTimeSlots timeSlots={guidedData['thu']} isLoading={isGuidedLoading} />
          )}
          {activeParcoursTab === '17.10' && (
            <GuidedTimeSlots timeSlots={guidedData['fri']} isLoading={isGuidedLoading} />
          )}
        </div>
      </div>
    </div>
  );
});

export default ExperienceTab;
